import { supabase } from '@/integrations/supabase/client';
import { FeatureKey, FeatureCheckResult, SubscriptionStatus, CachedFeatures } from '@/types/subscription';

export class SubscriptionService {
  
  /**
   * Fetches the current subscription for a restaurant.
   * Uses the cached_features column for performance.
   */
  static async getSubscription(restaurantId: string): Promise<SubscriptionStatus> {
    const { data, error } = await supabase
      .from('tenant_subscriptions')
      .select(`
        *,
        subscription_plans (
          name,
          code
        )
      `)
      .eq('restaurant_id', restaurantId)
      .single();

    if (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }

    if (!data) {
      throw new Error('No subscription found for this restaurant');
    }

    const isValid = ['active', 'trialing'].includes(data.status);
    
    // Parse cached features safely
    let features: CachedFeatures = {};
    if (data.cached_features && typeof data.cached_features === 'object') {
      features = data.cached_features as CachedFeatures;
    }

    return {
      isValid,
      status: data.status,
      planName: data.subscription_plans?.name || 'Unknown',
      trialEndsAt: data.trial_end,
      features
    };
  }

  /**
   * Checks if a specific feature is allowed and within limits.
   * This is a "Soft Enforcement" check for UI/UX.
   */
  static async checkFeatureAccess(
    restaurantId: string, 
    featureKey: FeatureKey,
    currentUsageCount?: number // Optional: pass current count to avoid extra query if known
  ): Promise<FeatureCheckResult> {
    
    try {
      const sub = await this.getSubscription(restaurantId);

      // 1. Check Status
      if (!sub.isValid) {
        return { allowed: false, reason: 'past_due' };
      }

      // 2. Get Feature Value
      const featureValue = sub.features[featureKey];

      // 3. Handle Boolean Features (Modules)
      if (typeof featureValue === 'boolean') {
        if (!featureValue) {
          return { allowed: false, reason: 'feature_disabled' };
        }
        return { allowed: true };
      }

      // 4. Handle Tier Features (String values like 'none', 'basic', 'advanced', 'enterprise')
      if (typeof featureValue === 'string') {
        // Tier features: 'none' means disabled, anything else means enabled
        if (featureValue === 'none') {
          return { allowed: false, reason: 'feature_disabled' };
        }
        // Return allowed with the tier level information
        return { allowed: true, tier: featureValue };
      }

      // 5. Handle Limit Features
      if (typeof featureValue === 'number') {
        const limit = featureValue;
        
        // Special case: 0 often means "unlimited" in our system
        // Or it could mean "not allowed" - depends on context
        // Let's treat 0 as unlimited for max_* features
        if (limit === 0 && featureKey.startsWith('max_')) {
          return { allowed: true, limit: Infinity };
        }
        
        // If usage not provided, we might need to fetch it (or just return the limit)
        // For simple checks, we just return the limit info
        if (currentUsageCount !== undefined) {
           if (currentUsageCount >= limit) {
             return { 
               allowed: false, 
               limit, 
               usage: currentUsageCount, 
               remaining: 0,
               reason: 'plan_limit' 
             };
           }
           return { 
             allowed: true, 
             limit, 
             usage: currentUsageCount, 
             remaining: limit - currentUsageCount 
           };
        }
        
        return { allowed: true, limit };
      }

      // Default fallback - feature not defined means disabled
      return { allowed: false, reason: 'feature_disabled' };

    } catch (error) {
      console.error('Error checking feature access:', error);
      return { allowed: false, reason: 'past_due' }; // Fail safe
    }
  }

  /**
   * Helper to get usage count for specific resources.
   * This queries the actual tables, not the subscription_usage cache (for accuracy).
   */
  static async getCurrentUsage(restaurantId: string, resource: 'branches' | 'staff' | 'menu_items' | 'categories' | 'monthly_orders'): Promise<number> {
    let count = 0;
    
    if (resource === 'branches') {
      const { count: c } = await supabase.from('branches').select('*', { count: 'exact', head: true }).eq('restaurant_id', restaurantId);
      count = c || 0;
    } else if (resource === 'staff') {
      const { count: c } = await supabase.from('staff_members').select('*', { count: 'exact', head: true }).eq('restaurant_id', restaurantId);
      count = c || 0;
    } else if (resource === 'categories') {
      // Count all categories across all branches of this restaurant
      const { count: c } = await supabase
        .from('categories')
        .select('*, branches!inner(restaurant_id)', { count: 'exact', head: true })
        .eq('branches.restaurant_id', restaurantId);
      count = c || 0;
    } else if (resource === 'menu_items') {
       // Count all menu items for categories belonging to branches of this restaurant
       const { count: c } = await supabase
        .from('menu_items')
        .select('*, categories!inner(branch_id, branches!inner(restaurant_id))', { count: 'exact', head: true })
        .eq('categories.branches.restaurant_id', restaurantId);
       count = c || 0;
    } else if (resource === 'monthly_orders') {
      // Count orders for the current calendar month across all branches
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      
      const { count: c } = await supabase
        .from('orders')
        .select('*, branches!inner(restaurant_id)', { count: 'exact', head: true })
        .eq('branches.restaurant_id', restaurantId)
        .gte('created_at', startOfMonth.toISOString())
        .lte('created_at', endOfMonth.toISOString());
      count = c || 0;
    }

    return count;
  }

  /**
   * Get usage count for a specific branch (for branch-level limits)
   */
  static async getBranchUsage(branchId: string, resource: 'categories' | 'menu_items'): Promise<number> {
    let count = 0;

    if (resource === 'categories') {
      const { count: c } = await supabase
        .from('categories')
        .select('*', { count: 'exact', head: true })
        .eq('branch_id', branchId);
      count = c || 0;
    } else if (resource === 'menu_items') {
      const { count: c } = await supabase
        .from('menu_items')
        .select('*, categories!inner(branch_id)', { count: 'exact', head: true })
        .eq('categories.branch_id', branchId);
      count = c || 0;
    }

    return count;
  }
}
